import {
  SESSION_AUDIT_ACTIONS,
  type SessionAuditAction,
  type SessionAuditListItem,
} from '@utazzo/shared';
import { UzBadge, UzDateRangePicker, type UzDateRange } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';

import { useSessionAudit } from '../use-session-audit';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/**
 * Admin-only viewer over the session/auth audit trail: login, failed login,
 * logout (manual / inactivity), idle warning and extend - each with IP, device
 * and geo-location. Filter by event type, date range and IP. Read-only; the
 * audited user never sees this.
 */

const EVENT_META: Record<
  SessionAuditAction,
  { label: string; tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  'auth.login': { label: 'Login', tone: 'success' },
  'auth.login.failed': { label: 'Failed login', tone: 'destructive' },
  'auth.logout': { label: 'Logout', tone: 'secondary' },
  'auth.logout.inactivity': { label: 'Auto logout (idle)', tone: 'warning' },
  'auth.idle.warning': { label: 'Idle warning', tone: 'warning' },
  'auth.idle.extended': { label: 'Extended', tone: 'default' },
  'auth.session.revoked': { label: 'Signed out other device', tone: 'secondary' },
};

const ACTION_OPTIONS = [
  { value: '', label: 'All events' },
  ...SESSION_AUDIT_ACTIONS.map((a) => ({ value: a, label: EVENT_META[a].label })),
] as const;

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'action',
    label: 'Event',
    options: ACTION_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

const parseAction = (value: string | undefined): SessionAuditAction | undefined =>
  value && (SESSION_AUDIT_ACTIONS as ReadonlyArray<string>).includes(value)
    ? (value as SessionAuditAction)
    : undefined;

const startOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};
const endOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/** Coarse "Browser on OS" label from a user-agent; falls back to the raw string. */
const deviceLabel = (ua: string | null): string => {
  if (!ua) return '-';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : null;
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad|iOS/.test(ua)
      ? 'iOS'
      : /Mac OS X|Macintosh/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser ?? os ?? ua;
};

const geoLabel = (geo: SessionAuditListItem['geo']): string => {
  if (!geo) return '-';
  const parts = [geo.city, geo.region, geo.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};

export const SessionAuditPage = (): React.JSX.Element => {
  const gridState = useDataGridState({ filterIds: ['action'] });
  const { state } = gridState;
  const [range, setRange] = useState<UzDateRange>({ from: null, to: null });

  const listQuery = useSessionAudit({
    page: state.page,
    pageSize: state.pageSize,
    sortDir: state.sortDir,
    action: parseAction(state.filters.action as string | undefined),
    search: state.q.trim() || undefined,
    from: range.from ? startOfDayIso(range.from) : undefined,
    to: range.to ? endOfDayIso(range.to) : undefined,
  });

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<SessionAuditListItem>> => {
    return [
      {
        id: 'performedAt',
        header: 'When',
        width: '190px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{formatWhen(row.performedAt)}</span>
        ),
      },
      {
        id: 'user',
        header: 'User',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">
              {row.userName ?? row.attemptedIdentifier ?? 'Unknown'}
            </span>
            <span className="text-xs text-zinc-500">
              {[row.userEmail, row.userPhone].filter(Boolean).join(' · ') ||
                (row.attemptedIdentifier ? `attempted: ${row.attemptedIdentifier}` : '-')}
            </span>
          </div>
        ),
      },
      {
        id: 'action',
        header: 'Event',
        width: '170px',
        cell: (row) => {
          const meta = EVENT_META[row.action];
          return (
            <UzBadge tone={meta.tone} pill>
              {meta.label}
            </UzBadge>
          );
        },
      },
      {
        id: 'ipAddress',
        header: 'IP address',
        width: '150px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{row.ipAddress ?? '-'}</span>
        ),
      },
      {
        id: 'location',
        header: 'Location',
        width: '190px',
        cell: (row) => <span className="text-sm text-zinc-700">{geoLabel(row.geo)}</span>,
      },
      {
        id: 'device',
        header: 'Device',
        width: '170px',
        cell: (row) => (
          <span className="text-sm text-zinc-600" title={row.userAgent ?? undefined}>
            {deviceLabel(row.userAgent)}
          </span>
        ),
      },
      {
        id: 'reason',
        header: 'Details',
        width: '160px',
        cell: (row) => <span className="text-sm text-zinc-500">{row.reason ?? '-'}</span>,
      },
    ];
  }, []);

  return (
    <PageShell
      title="Session audit"
      description="Every sign-in, failed sign-in, sign-out (manual and inactivity), idle warning and extend - with IP, device and location. Admin-only."
    >
      <div className="mb-3">
        <UzDateRangePicker
          value={range}
          onValueChange={setRange}
          placeholder="Filter by date range"
          triggerClassName="w-64"
        />
      </div>

      <DataGrid
        gridId="session-audit"
        data={rows}
        columns={columns}
        filters={STATIC_FILTERS}
        getRowId={(row) => row.id}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        searchPlaceholder="Search by email, phone or IP"
        emptyTitle="No session activity"
        emptyDescription="Sign-ins, sign-outs and idle events appear here as they happen."
        tableLayout="auto"
      />
    </PageShell>
  );
};
